import { AttributedSummary } from '@/components/session/AttributedSummary';
import { HeadingText } from '@/components/session/note-text/HeadingText';
import { splitHeadings } from '@/components/session/note-text/noteHeadings';
import { Textarea } from '@/components/ui/textarea';
import { type KeyboardEvent, type MouseEvent, useLayoutEffect, useRef } from 'react';
import { NoteDiffView } from './NoteDiffView';
import { noteTextClasses, noteTextareaClasses } from './noteInstructions.styles';
import type { ComposerNoteBodyProps } from './noteInstructions.types';

/** Characters from the start of ``container`` to the clicked point, or null when it isn't inside. */
const caretOffset = (container: HTMLElement): number | null => {
  const selection = window.getSelection();
  if (!selection?.anchorNode || !container.contains(selection.anchorNode)) return null;
  const range = document.createRange();
  range.selectNodeContents(container);
  range.setEnd(selection.anchorNode, selection.anchorOffset);
  return range.toString().length;
};

// Attributed phrases play their audio on click.
const isAttributedPhrase = (target: EventTarget) =>
  target instanceof HTMLElement && !!target.closest('[role="button"]');

/**
 * The composer panel's note: a just-applied change as a diff, otherwise the note with its
 * section headings. Clicking it opens a borderless textarea, sized to the note, at the clicked point.
 */
export const ComposerNoteBody = ({
  text,
  change,
  onDismissChange,
  attribution,
  isEditing,
  onEdit,
  onStartEdit,
  onEditBlur,
}: ComposerNoteBodyProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Where the user clicked, until the textarea that replaces the read view takes the caret.
  const pendingCaret = useRef<number | null>(null);
  const isTextareaShown = !change && isEditing && !!onEdit;

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const caret = pendingCaret.current;
    if (!isTextareaShown || !textarea || caret == null) return;
    pendingCaret.current = null;
    // The textarea is as tall as the read view was, so the panel already shows the clicked line.
    textarea.focus({ preventScroll: true });
    textarea.setSelectionRange(caret, caret);
  }, [isTextareaShown]);

  if (change) return <NoteDiffView key={change.id} change={change} onDismiss={onDismissChange} />;
  if (isTextareaShown) {
    return (
      <Textarea
        ref={textareaRef}
        value={text}
        onChange={(event) => onEdit?.(event.target.value)}
        onBlur={onEditBlur}
        className={noteTextareaClasses}
        aria-label="Nóta"
        placeholder="Nóta..."
      />
    );
  }

  const startEdit = (caret: number | null) => {
    pendingCaret.current = caret ?? text.length;
    onStartEdit();
  };
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // A drag selects text to copy; only a plain click opens editing.
    if (!onEdit || isAttributedPhrase(event.target) || window.getSelection()?.isCollapsed === false) return;
    startEdit(caretOffset(event.currentTarget));
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!onEdit || event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    startEdit(null);
  };

  return (
    <section
      data-testid="composer-note"
      className={noteTextClasses}
      tabIndex={onEdit ? 0 : undefined}
      aria-label={onEdit ? 'Nóta — smelltu eða ýttu á Enter til að breyta' : 'Nóta'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {attribution ? (
        <AttributedSummary
          attributedSummary={attribution.data}
          onHighlightSegments={attribution.onHighlightSegments}
          onSeekToTime={attribution.onSeekToTime}
          headings
        />
      ) : (
        <HeadingText pieces={splitHeadings([text])[0]} />
      )}
    </section>
  );
};
